import { canv } from "@/canv/global";
import {
    getColorProperty,
    getNumberProperty,
    getStringProperty,
    isOnScreenAfterTranslate,
} from "@/canv/utils";
import { WIDTH_MULTIPLIER_FOR_HIGHLIGHTER } from "@/constants";
import { elementBase } from "@/elementBase/elementBase";
import type {
    BezierArea,
    ControlPoint,
    Point,
    SketchMode,
    SketchWidgetFixReason,
    SketchWidgetFixState,
    SketchWidgetStateMap,
} from "@/elements/sketch/types";
import { widgetOption } from "@/widgetMenu/widgetMenu";

export class SketchWidget extends elementBase {
    constructor() {
        super();
        this.updateDims();

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    updateDims(): void {
        if (this.points.length < 2) {
            return;
        }

        let leftX = this.points[0].x;
        let rightX = this.points[0].x;
        let topY = this.points[0].y;
        let bottomY = this.points[0].y;

        for (let i = 0; i < this.points.length; i++) {
            leftX = this.points[i].x < leftX ? this.points[i].x : leftX;
            rightX = this.points[i].x > rightX ? this.points[i].x : rightX;
            topY = this.points[i].y < topY ? this.points[i].y : topY;
            bottomY = this.points[i].y > bottomY ? this.points[i].y : bottomY;
        }

        this.startX = leftX;
        this.startY = topY;
        this.width = rightX - leftX;
        this.height = bottomY - topY;

        this.calcControlPoints();

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    calcControlPoints(): void {
        const points: [number, number][] = [];
        for (let i = 0; i < this.points.length; i++) {
            points.push([this.points[i].x, this.points[i].y]);
        }

        // Default tension of one-quarter gives nice results
        const tension = 0.25;

        const l = points.length;

        if (l < 5) return;

        function h(x: number, y: number): number {
            return Math.sqrt(x * x + y * y);
        }

        const cpoints: ControlPoint[] = [];
        points.forEach(() => {
            cpoints.push({});
        });

        for (let i = 1; i < l - 1; i++) {
            const pi = points[i]; // current point
            const pp = points[i - 1]; // previous point
            const pn = points[i + 1]; // next point;

            /* First, we calculate the normalized tangent slope vector (dx,dy).
             * We intentionally don't work with the derivative so we don't have
             * to handle the vertical line edge cases separately. */

            const rdx = pn[0] - pp[0]; // actual delta-x between previous and next points
            const rdy = pn[1] - pp[1]; // actual delta-y between previous and next points
            const rd = h(rdx, rdy); // actual distance between previous and next points
            const dx = rdx / rd; // normalized delta-x (so the total distance is 1)
            const dy = rdy / rd; // normalized delta-y (so the total distance is 1)

            /* Next we calculate distances to previous and next points, so we
             * know how far out to put the control points on the tangents (tension).
             */

            const dp = h(pi[0] - pp[0], pi[1] - pp[1]); // distance to previous point
            const dn = h(pi[0] - pn[0], pi[1] - pn[1]); // distance to next point

            /* Now we can calculate control points. Previous control point is
             * located on the tangent of the curve, with the distance between it
             * and the current point being a fraction of the distance between the
             * current point and the previous point. Analogous to next point. */

            const cpx = pi[0] - dx * dp * tension;
            const cpy = pi[1] - dy * dp * tension;
            const cnx = pi[0] + dx * dn * tension;
            const cny = pi[1] + dy * dn * tension;

            cpoints[i] = {
                cp: [cpx, cpy], // previous control point
                cn: [cnx, cny], // next control point
            };
        }

        /* For the end points, we only need to calculate one control point.
         * Picking a point in the middle between the endpoint and the other's
         * control point seems to work well. */

        if (l === 2) {
            cpoints[0] = {
                cn: [points[0][0] + (points[1][0] - points[0][0]) / 2, points[0][1]],
            };
            cpoints[l - 1] = {
                cp: [points[0][0] + (points[1][0] - points[0][0]) / 2, points[1][1]],
            };
        } else {
            const firstCp = cpoints[1].cp ?? [0, 0];
            cpoints[0] = {
                cn: [(points[0][0] + firstCp[0]) / 2, (points[0][1] + firstCp[1]) / 2],
            };
            const lastCn = cpoints[l - 2].cn ?? [0, 0];
            cpoints[l - 1] = {
                cp: [(points[l - 1][0] + lastCn[0]) / 2, (points[l - 1][1] + lastCn[1]) / 2],
            };
        }

        this.cpoints = cpoints;
    }

    draw(): void {
        if (this.color === "transparent") {
            return;
        }

        if (this.cpoints.length < this.points.length) {
            return;
        }

        canv.ctx.beginPath();

        const globalAlphaMem = canv.ctx.globalAlpha;
        canv.ctx.globalAlpha = 1;
        canv.ctx.lineWidth = this.lineWidth;
        canv.ctx.strokeStyle = this.color;
        canv.ctx.fillStyle = this.color;

        if (this.mode === "highlighter") {
            canv.ctx.globalAlpha = 0.3;
            canv.ctx.lineWidth *= WIDTH_MULTIPLIER_FOR_HIGHLIGHTER;
        }

        canv.ctx.moveTo(this.points[0].x, this.points[0].y);

        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return;
            const p = this.points[i];
            const cp = this.cpoints[i];
            const cpp = this.cpoints[i - 1];

            if (cpp.cn && cp.cp) {
                canv.ctx.bezierCurveTo(cpp.cn[0], cpp.cn[1], cp.cp[0], cp.cp[1], p.x, p.y);
            }
        }

        canv.ctx.stroke();
        canv.ctx.closePath();

        canv.ctx.globalAlpha = globalAlphaMem;
    }

    translate(x: number, y: number): void {
        if (isOnScreenAfterTranslate(x, y, this)) {
            for (let i = 0; i < this.points.length; i++) {
                this.points[i].x += x;
                this.points[i].y += y;
            }
        }

        this.updateDims();
    }

    resize(x: number, y: number): void {
        if (x === 0 && y === 0) return;
        if (this.width + x < 15 || this.height + y < 15) return;

        const scaleX = this.width !== 0 ? (this.width + x) / this.width : 1;
        const scaleY = this.height !== 0 ? (this.height + y) / this.height : 1;

        let scale = 1;
        if (scaleX === 1 || scaleY === 1) {
            // случай когда тянем вбок в группе
            if (scaleX === 1) scale = scaleY;
            if (scaleY === 1) scale = scaleX;
        } else {
            scale = Math.min(scaleX, scaleY);
        }

        for (let i = 1; i < this.points.length; i++) {
            const relX = this.points[i].x - this.points[0].x;
            const relY = this.points[i].y - this.points[0].y;

            this.points[i].x = this.points[0].x + relX * scale;
            this.points[i].y = this.points[0].y + relY * scale;
        }

        this.updateDims();
    }

    isInCircleArea(area: BezierArea): boolean {
        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return false;
            const p = this.points[i];
            const cp = this.cpoints[i];
            const cpp = this.cpoints[i - 1];

            if (!cpp.cn || !cp.cp) continue;

            const res = this.isIntersectBezierLineWithCircle(
                this.points[i - 1].x,
                this.points[i - 1].y,
                cpp.cn[0],
                cpp.cn[1],
                cp.cp[0],
                cp.cp[1],
                p.x,
                p.y,
                area,
            );

            if (res) {
                return true;
            }
        }

        return false;
    }

    isIntersectBezierLineWithCircle(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
        area: BezierArea,
    ): boolean {
        const iterates = 1000;
        const step = 1 / iterates;
        for (let t = 0; t < 1; t += step) {
            const res = this.getBezierPoint(x1, y1, x2, y2, x3, y3, x4, y4, t);
            const distance = Math.sqrt((res.x - area.x) ** 2 + (res.y - area.y) ** 2);

            if (distance <= area.radius) {
                return true;
            }
        }

        return false;
    }

    isInArea(area: BezierArea): boolean {
        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return false;
            const p = this.points[i];
            const cp = this.cpoints[i];
            const cpp = this.cpoints[i - 1];

            if (!cpp.cn || !cp.cp) continue;

            const res = this.isIntersectBezierLine(
                this.points[i - 1].x,
                this.points[i - 1].y,
                cpp.cn[0],
                cpp.cn[1],
                cp.cp[0],
                cp.cp[1],
                p.x,
                p.y,
                area,
            );

            if (res) {
                return true;
            }
        }

        return false;
    }

    isOver(): boolean {
        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return false;
            const p = this.points[i];
            const cp = this.cpoints[i];
            const cpp = this.cpoints[i - 1];

            if (!cpp.cn || !cp.cp) continue;

            const res = this.isOverBezierLine(
                this.points[i - 1].x,
                this.points[i - 1].y,
                cpp.cn[0],
                cpp.cn[1],
                cp.cp[0],
                cp.cp[1],
                p.x,
                p.y,
            );

            if (res) {
                return true;
            }
        }

        return false;
    }

    isOverBezierLine(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
    ): boolean {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const iterates = 1000;
        const step = 1 / iterates;
        for (let t = 0; t < 1; t += step) {
            const res = this.getBezierPoint(x1, y1, x2, y2, x3, y3, x4, y4, t);
            const distance = Math.sqrt((res.x - x) * (res.x - x) + (res.y - y) * (res.y - y));
            if (distance <= this.lineWidth) {
                return true;
            }
        }

        return false;
    }

    isIntersectBezierLine(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
        area: BezierArea,
    ): boolean {
        const width = area.width ?? 0;
        const height = area.height ?? 0;

        const iterates = 1000;
        const step = 1 / iterates;
        for (let t = 0; t < 1; t += step) {
            const res = this.getBezierPoint(x1, y1, x2, y2, x3, y3, x4, y4, t);
            if (
                res.x > area.x &&
                res.x < area.x + width &&
                res.y > area.y &&
                res.y < area.y + height
            ) {
                return true;
            }
        }

        return false;
    }

    getBezierPoint(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
        t = 0.5,
    ): Point {
        const result: Point = {
            x: 0,
            y: 0,
        };

        result.x =
            (1 - t) * (1 - t) * (1 - t) * x1 +
            3 * (1 - t) * (1 - t) * t * x2 +
            3 * (1 - t) * t * t * x3 +
            t * t * t * x4;
        result.y =
            (1 - t) * (1 - t) * (1 - t) * y1 +
            3 * (1 - t) * (1 - t) * t * y2 +
            3 * (1 - t) * t * t * y3 +
            t * t * t * y4;

        return result;
    }

    menuColor(color: string): void {
        this.color = color;
        const penTool = canv.toolBox.TryGetToolByName("penTool");
        penTool.setColor(this.color);
    }

    menuWidth(value: number): void {
        this.lineWidth = value;
        this.updateDims();
        const penTool = canv.toolBox.TryGetToolByName("penTool");
        penTool.setWidth(this.lineWidth);
    }

    getWidgetOptions(): widgetOption[] {
        const widgetOptions: widgetOption[] = [];
        widgetOptions.push(new widgetOption("color", this.color, this.UID));
        widgetOptions.push(new widgetOption("width", this.lineWidth, this.UID));

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "color") {
            return this.menuColor;
        } else if (name === "width") {
            return this.menuWidth;
        }
    }

    extractPoints(): Point[] {
        const res: Point[] = [];
        for (let i = 0; i < this.points.length; i++) {
            const point: Point = {
                x: this.points[i].x,
                y: this.points[i].y,
            };
            res.push(point);
        }

        return res;
    }

    loadPoints(points: Point[]): void {
        if (points === undefined) return;
        this.points = [];
        for (let i = 0; i < points.length; i++) {
            const newPoint: Point = {
                x: getNumberProperty("x", points[i]),
                y: getNumberProperty("y", points[i]),
            };
            this.points.push(newPoint);
        }
    }

    getFixType(reason: SketchWidgetFixReason): SketchWidgetFixState {
        switch (reason) {
            case "move":
                return "move";
            case "resize":
                return "resize";
            case "wm_color":
                return "color";
            case "wm_width":
                return "width";
            default:
                return "full";
        }
    }

    getState<T extends SketchWidgetFixState>(fixType: T): SketchWidgetStateMap[T] {
        switch (fixType) {
            case "move":
                return {
                    points: this.extractPoints(),
                } as SketchWidgetStateMap[T];
            case "resize":
                return {
                    points: this.extractPoints(),
                } as SketchWidgetStateMap[T];
            case "color":
                return {
                    color: this.color,
                } as SketchWidgetStateMap[T];
            case "width":
                return {
                    lineWidth: this.lineWidth,
                } as SketchWidgetStateMap[T];
            case "full":
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "sketchWidget",
                    color: this.color,
                    points: this.extractPoints(),
                    lineWidth: this.lineWidth,
                    mode: this.mode,
                } as SketchWidgetStateMap[T];
            default:
                return {} as never;
        }
    }

    loadState<T extends SketchWidgetFixState>(stateObj: SketchWidgetStateMap[T], fixType: T): void {
        switch (fixType) {
            case "move": {
                this.loadPoints((stateObj as SketchWidgetStateMap["move"]).points);
                break;
            }
            case "resize": {
                this.loadPoints((stateObj as SketchWidgetStateMap["resize"]).points);
                break;
            }
            case "color": {
                this.color = getColorProperty("color", stateObj);
                break;
            }
            case "width": {
                this.lineWidth = getNumberProperty("lineWidth", stateObj);
                break;
            }
            case "full": {
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);
                this.color = getColorProperty("color", stateObj);
                this.loadPoints((stateObj as SketchWidgetStateMap["full"]).points);
                this.lineWidth = getNumberProperty("lineWidth", stateObj);
                this.mode = getStringProperty("mode", stateObj) as SketchMode;
                break;
            }
        }

        if (this.lineWidth === 0) this.lineWidth = 1;

        this.updateDims();
    }

    constructorName = "sketch";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
    points: Point[] = [];
    cpoints: ControlPoint[] = [];

    lineWidth = 1;
    color = "black";
    mode: SketchMode = "pen";
}
